import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Config from '../config/Config';
import { useLanguage } from './LanguageContext';
import { useCurrentUser } from './CurrentUserContext';
import { translations } from '../translations';
import { useApi } from '../hooks/useApi';
import { handleSessionExpired } from '../utils/api';
import { getCrmToken } from '../utils/externalAccess';

// Close codes the backend uses on the log socket: token missing or rejected, and role not allowed.
const WS_AUTH_FAILED_CODE = 4401;
const WS_FORBIDDEN_CODE = 4403;
const MAX_RECONNECTS = 5;
const RECONNECT_DELAY_MS = 2000;
const PING_INTERVAL_MS = 5000;
const ACTIVE_BUILD_POLL_MS = 10000;
const SOCKET_CONTROL_TYPES = new Set(['pong', 'auth_error', 'forbidden', 'status', 'error']);

const webSocketUrl = Config.WebSocketUrl;

// Build output is plain text, so a chunk can happen to be valid JSON ("42", "true"). Only
// objects with a known control type count as control messages; everything else is log text.
const parseControlMessage = (raw) => {
    try {
        const data = JSON.parse(raw);
        return data && typeof data === 'object' && SOCKET_CONTROL_TYPES.has(data.type) ? data : null;
    } catch {
        return null;
    }
};

const NO_BUILD = { id: '', flavorName: '' };
const BuildLogContext = createContext(null);

/**
 * Owns the build log socket so the log survives page changes.
 *
 * The server keeps building when the browser leaves a page and replays the whole log on every
 * connection, so the only thing to keep alive is one socket and its text. Two things can name the
 * build to follow: the /builder/:id page (`page`), and otherwise the build that was running when the
 * user left, or that the server reports as running (`dock`). The page wins while it is open.
 *
 * Only one build runs at a time on the server, so one stream is enough. Opening an old, finished
 * build on its page replaces the stream for as long as that page is open.
 */
export function BuildLogProvider({ children }) {
    const { language } = useLanguage();
    const { can } = useCurrentUser();
    // The stream outlives language changes, so it reads strings through a ref instead of re-subscribing.
    const labels = useRef(translations[language]);
    labels.current = translations[language];

    const [page, setPage] = useState(NO_BUILD);
    const [dock, setDock] = useState(NO_BUILD);
    const [logs, setLogs] = useState('');
    const [status, setStatusState] = useState('idle');
    const [error, setError] = useState('');
    const statusRef = useRef('idle');
    const pageRef = useRef(NO_BUILD);
    pageRef.current = page;

    const setStatus = useCallback((next) => {
        statusRef.current = next;
        setStatusState(next);
    }, []);

    const streamId = page.id || dock.id;
    const flavorName = page.id ? page.flavorName : dock.flavorName;

    const canBuild = can('build:run');
    const { data: activeData, refresh: refreshActiveBuild } = useApi('/active-build', { pollMs: ACTIVE_BUILD_POLL_MS, enabled: canBuild });
    const activeBuild = canBuild ? activeData?.active || null : null;
    // useApi makes a new refresh function every render; consumers put this one in effect dependencies.
    const refreshRef = useRef(refreshActiveBuild);
    refreshRef.current = refreshActiveBuild;
    const refreshActive = useCallback(() => refreshRef.current(), []);

    // A build the server reports as running becomes the one in the dock, unless a build page is
    // open or the dock already follows a live build.
    useEffect(() => {
        if (!activeBuild?.session_id || page.id || dock.id === activeBuild.session_id) return;
        if (dock.id && statusRef.current === 'running') return;
        setDock({ id: activeBuild.session_id, flavorName: activeBuild.flavorName || '' });
    }, [activeBuild, page.id, dock.id]);

    const follow = useCallback((id, name = '') => setPage({ id, flavorName: name }), []);

    // Leaving a build page keeps a running build in the dock; a finished one was already read there.
    const release = useCallback(() => {
        if (statusRef.current === 'running') setDock(pageRef.current);
        else setDock(NO_BUILD);
        setPage(NO_BUILD);
    }, []);

    const dismiss = useCallback(() => setDock(NO_BUILD), []);

    // One socket per build being followed. The server replays the whole log on every connection, so
    // each (re)connect starts from an empty log instead of appending a second copy.
    useEffect(() => {
        setLogs('');
        setError('');
        if (!streamId) {
            setStatus('idle');
            return undefined;
        }

        setStatus('running');
        let cancelled = false;
        let finished = false;
        let attempt = 0;
        let socket;
        let heartbeat;
        let retryTimer;

        const finish = (next) => {
            finished = true;
            setStatus(next);
        };

        const connect = () => {
            const token = getCrmToken();
            const tokenQuery = token ? `?token=${encodeURIComponent(token)}` : '';
            socket = new WebSocket(`${webSocketUrl}ws/log/${encodeURIComponent(streamId)}${tokenQuery}`);

            socket.onopen = () => {
                if (cancelled) return;
                attempt = 0;
                setLogs('');
                setError('');
                heartbeat = setInterval(() => {
                    if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: 'ping' }));
                }, PING_INTERVAL_MS);
            };

            socket.onmessage = (event) => {
                if (cancelled) return;
                const control = parseControlMessage(event.data);
                if (!control) {
                    setLogs((prev) => prev + event.data);
                    return;
                }
                if (control.type === 'auth_error') {
                    handleSessionExpired();
                } else if (control.type === 'forbidden') {
                    finish('failed');
                    setError(labels.current.noBuildAccess);
                } else if (control.type === 'status') {
                    finish(control.status);
                    setLogs((prev) => `${prev}${control.status === 'success' ? labels.current.buildSucceeded : labels.current.buildFailed}\n`);
                } else if (control.type === 'error') {
                    finish('failed');
                    setError(control.message || labels.current.webSocketError);
                }
            };

            socket.onclose = (event) => {
                clearInterval(heartbeat);
                if (cancelled || finished) return;
                if (event.code === WS_AUTH_FAILED_CODE) {
                    handleSessionExpired();
                    return;
                }
                if (event.code === WS_FORBIDDEN_CODE) return;
                if (attempt >= MAX_RECONNECTS) {
                    setError(labels.current.webSocketError);
                    return;
                }
                attempt += 1;
                retryTimer = setTimeout(connect, RECONNECT_DELAY_MS * attempt);
            };
        };

        connect();
        return () => {
            cancelled = true;
            clearTimeout(retryTimer);
            clearInterval(heartbeat);
            if (socket) socket.close();
        };
    }, [streamId, setStatus]);

    const value = useMemo(() => ({
        streamId, flavorName, logs, status, error, activeBuild, follow, release, dismiss, refreshActive,
    }), [streamId, flavorName, logs, status, error, activeBuild, follow, release, dismiss, refreshActive]);

    return <BuildLogContext.Provider value={value}>{children}</BuildLogContext.Provider>;
}

export function useBuildLog() {
    const context = useContext(BuildLogContext);
    if (!context) throw new Error('useBuildLog must be used within BuildLogProvider');
    return context;
}
